import { useState } from 'react';
import { AlertTriangle, CheckCircle2, Code, Loader2, Play, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { bearerAuthHeader } from '../utils/apiHeaders';

/**
 * System Admin › Dev Tools: health checks against the running platform
 * (RESTORE_PLAN R3.6). The old checks called four `/dev/*` routes that scanned
 * source files, which an edge function cannot read, and that no longer
 * existed; those code checks run in CI on every PR (scripts/validate-imports.js,
 * lint, the build). Two checks come from the retired BillingAdminTestHelper:
 * each firm has a Billing Admin, and the upgrade-request routes answer.
 */

export interface CheckResult {
  check: string;
  status: 'pass' | 'warn' | 'fail';
  detail: string;
}

/** One result per firm: who its Billing Admins are, or that it has none. */
export function billingAdminResults(firms: Array<{ id: string; name?: string }>, adminsByFirm: Record<string, Array<{ email?: string; name?: string }> | null>): CheckResult[] {
  return firms.map((f) => {
    const admins = adminsByFirm[f.id];
    const name = f.name || f.id;
    if (admins === null || admins === undefined) return { check: `Billing Admin: ${name}`, status: 'fail', detail: 'The billing-admins route did not answer' };
    if (admins.length === 0) return { check: `Billing Admin: ${name}`, status: 'warn', detail: 'No Billing Admin: upgrade requests have no one to approve them' };
    return { check: `Billing Admin: ${name}`, status: 'pass', detail: admins.map((a) => a.email || a.name || 'unnamed').join(', ') };
  });
}

export const CI_CODE_CHECKS = ['node scripts/validate-imports.js', 'npm run lint', 'npx vite build', 'npx vitest run', 'deno test tests/server/'];

const API = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;
const ICON = { pass: CheckCircle2, warn: AlertTriangle, fail: XCircle } as const;
const TONE = { pass: 'text-green-600', warn: 'text-amber-600', fail: 'text-red-600' } as const;

export function DevTools({ accessToken: _accessToken }: { accessToken: string }) {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<CheckResult[]>([]);
  const [lastRun, setLastRun] = useState<Date | null>(null);

  const get = async (path: string) => {
    const res = await fetch(`${API}${path}`, { headers: { ...bearerAuthHeader() } });
    const body = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, body };
  };

  const runChecks = async () => {
    setRunning(true);
    const out: CheckResult[] = [];
    try {
      const health = await get('/health');
      out.push(health.ok ? { check: 'Server', status: 'pass', detail: `Answering (${health.body.version ?? 'no version'})` } : { check: 'Server', status: 'fail', detail: `Health check returned ${health.status}` });

      const firmsRes = await get('/consulting-firms');
      const firms: Array<{ id: string; name?: string }> = Array.isArray(firmsRes.body.firms) ? firmsRes.body.firms : [];
      if (!firmsRes.ok) out.push({ check: 'Consulting firms', status: 'fail', detail: `The firm list returned ${firmsRes.status}` });

      const adminsByFirm: Record<string, any[] | null> = {};
      const upgradeFailures: string[] = [];
      for (const f of firms) {
        const admins = await get(`/billing-admins/${encodeURIComponent(f.id)}`);
        adminsByFirm[f.id] = admins.ok ? (admins.body.billingAdmins ?? []) : null;
        const upgrades = await get(`/upgrade-requests/${encodeURIComponent(f.id)}`);
        if (!upgrades.ok) upgradeFailures.push(`${f.name || f.id} (${upgrades.status})`);
      }
      out.push(...billingAdminResults(firms, adminsByFirm));
      out.push(upgradeFailures.length === 0
        ? { check: 'Upgrade-request routes', status: 'pass', detail: `Answered for ${firms.length} ${firms.length === 1 ? 'firm' : 'firms'}` }
        : { check: 'Upgrade-request routes', status: 'fail', detail: `No answer for ${upgradeFailures.join(', ')}` });

      const integrity = await get('/admin/data-integrity');
      out.push(!integrity.ok
        ? { check: 'Records and orphans', status: 'fail', detail: `Returned ${integrity.status}` }
        : integrity.body.issueCount === 0
          ? { check: 'Records and orphans', status: 'pass', detail: 'No orphans or mismatches' }
          : { check: 'Records and orphans', status: 'warn', detail: `${integrity.body.issueCount} issues: see Operations › Records and orphans` });
    } catch (e: any) {
      out.push({ check: 'Checks', status: 'fail', detail: e?.message || 'The checks could not run' });
    } finally {
      setResults(out);
      setLastRun(new Date());
      setRunning(false);
      const failed = out.filter((r) => r.status === 'fail').length;
      if (failed) toast.error(`${failed} ${failed === 1 ? 'check' : 'checks'} failed`);
      else toast.success('Platform checks done');
    }
  };

  return (
    <div className="space-y-6" data-testid="dev-tools">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Platform checks</CardTitle>
              <CardDescription>The server answers; every firm has a Billing Admin; the upgrade-request routes answer; no orphaned records</CardDescription>
            </div>
            <Button onClick={runChecks} disabled={running}>
              {running ? <Loader2 className="size-4 mr-2 animate-spin" aria-hidden="true" /> : <Play className="size-4 mr-2" aria-hidden="true" />}
              Run checks
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {results.length === 0 ? (
            <p className="text-muted-foreground">Not run yet.</p>
          ) : (
            <ul className="divide-y rounded border" data-testid="dev-check-results">
              {results.map((r, i) => {
                const Icon = ICON[r.status];
                return (
                  <li key={i} className="flex items-start gap-3 p-3">
                    <Icon className={`size-4 mt-0.5 ${TONE[r.status]}`} aria-hidden="true" />
                    <div className="flex-1"><p>{r.check}</p><p className="text-muted-foreground">{r.detail}</p></div>
                    <Badge variant="outline">{r.status}</Badge>
                  </li>
                );
              })}
            </ul>
          )}
          {lastRun && <p className="text-muted-foreground mt-2">Last run {lastRun.toLocaleTimeString()}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Code className="size-5" aria-hidden="true" />Code checks</CardTitle>
          <CardDescription>Import health, React imports and the pre-deployment checks run in CI on every pull request, before a merge</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="list-disc pl-5 text-muted-foreground">
            {CI_CODE_CHECKS.map((c) => <li key={c}><code>{c}</code></li>)}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
